import { AddProjectContentItemInput } from '@deltek/specification-client/core/Models/projectProductProperty'
import { OptionItemState } from '@deltek/specification-client/core/Models/textContentElement'
import { buildContentElementsForSyncQuery } from 'components/WorkspaceEditor/features/bestPractices'
import { PRODUCT_TYPE_PROPERTY_NODE_TYPE } from 'components/WorkspaceEditor/features/bestPractices/constants'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { DataNode } from 'components/WorkspaceEditor/types/types'
import { useWorkspace2Store } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackChangeType } from 'models/changes'
import { useCallback } from 'react'

/**
 * Hierarchical content element type with children support.
 * Matches the structure used in DraggableItem.tsx
 * Uses Partial<TextContentElementInput> since not all properties are required
 */
type HierarchicalContentElement = Partial<ElementData> & {
    children?: HierarchicalContentElement[]
    userAddedContentBP?: Partial<AddProjectContentItemInput>
}

export type ElementData = {
    id: string
    imperialUnits?: string
    metricUnits?: string
    optionItemState?: OptionItemState
    text?: string
    type?: string
    placeholderText?: string
    changeType?: TrackChangeType
    authoredUserId?: string
    elements?: ElementData[]
    hyperlinkUrl?: string
    userAddedContentBP?: Partial<AddProjectContentItemInput>
}

type HierarchyMapEntry = {
    node: DataNode
    parent?: DataNode
    contentItemView?: Partial<ContentItemViewNodesType>
}

/**
 * Extracts a unique key from text value.
 * Adapted from getUniqueKey in utils.ts
 */
const getUniqueKey = (text: string): string | undefined => {
    if (!text) return undefined
    // Extract key pattern like "KEY:" or similar
    const match = text.match(/^([A-Z0-9_]+):/i)
    return match ? match[1] : undefined
}

/**
 * Gets parent key from parent's text or key.
 * Adapted from getParentKey in utils.ts
 */
const getParentKey = (parentKey: string): string => {
    if (!parentKey) return ''
    return parentKey.split(':')[0] || ''
}

/**
 * Combines unique key with parent key to form actual property key.
 * Adapted from getActualKeyWithParent in utils.ts
 */
const getActualKeyWithParent = (
    uniqueKey: string,
    parentKey: string
): string => {
    if (!uniqueKey) return ''
    if (!parentKey) return uniqueKey
    return `${parentKey}:${uniqueKey}`
}

/**
 * Determines the content item owner type based on node and parent context.
 * Adapted from getContentOwnerType in draggableItemUtils.ts
 */
const getContentOwnerType = (
    node: Partial<ContentItemViewNodesType>,
    parent?: Partial<ContentItemViewNodesType>
): string => {
    // If node already has contentItemOwnerType, use it
    if (node.contentItemOwnerType) {
        return node.contentItemOwnerType
    }

    // Determine based on parent type and node characteristics
    if (node.isSharedProductTypeProperty || node.isProductTypeNested) {
        return 'ProductTypeProperty'
    }

    if (parent?.type === 'Nugget' || !parent) {
        return 'Nugget'
    }

    return parent?.type || 'ContentItem'
}

/**
 * Determines the text type (Article vs Paragraph) based on node hierarchy.
 * Article = top-level (no parent or hasTitle), Paragraph = has parent
 */
const getTextType = (
    node: Partial<ContentItemViewNodesType>,
    hasParent: boolean
): string => {
    // If it's a top-level node without parent, it's an Article
    if (!hasParent) {
        return 'Article'
    }
    return 'Paragraph'
}

/**
 * Determines the product type property type.
 */
const getProductTypePropertyType = (
    node: Partial<ContentItemViewNodesType>
): string | undefined => {
    if (node.isSharedProductTypeProperty) {
        return 'Shared'
    }
    if (node.isProductType || node.isForProductType) {
        return 'Specific'
    }
    return undefined
}

/**
 * Calculates the parent key for property key generation.
 * Adapted from calculateParentKey in draggableItemUtils.ts
 */
const calculateParentKey = (
    parent?: Partial<ContentItemViewNodesType>
): string => {
    if (!parent) return ''

    // Check if parent is user-added and has no parentKey
    if (parent.isUserAdded && !parent.propertyRefId) {
        const parentElementText = parent.elements?.[0]?.text
        return parentElementText ? getParentKey(String(parentElementText)) : ''
    }

    return parent.propertyRefId ? getParentKey(parent.propertyRefId) : ''
}

/**
 * Creates input configuration for article-level content.
 * Adapted from createArticleInput in draggableItemUtils.ts
 */
const createArticleInput = (
    node: Partial<ContentItemViewNodesType>,
    text: string
): Partial<AddProjectContentItemInput> => {
    const isPTGroupArticleNugget =
        node.partType === 'Part' && node.isProductType

    const input: Partial<AddProjectContentItemInput> = {
        contentItemOwnerType: 'Nugget',
        contentItemParentId: node.dtvContentItemParentId,
        contentItemParentType: 'None',
        index: node.order,
        partType: node.partType,
        textType: 'Article',
        productTypePropertyType: undefined,
    }

    if (!isPTGroupArticleNugget) {
        input.contentItemOwnerType = 'ProductTypeProperty'
        input.propertyKey = text
        input.productTypePropertyType = 'Shared'
    }

    return input
}

// Resolves the paragraph's content-owner type by matching
// dtvContentItemParentId against dtvItemId in the flat list. Shared PT
// Properties merge into a Nugget (skipping the PT Property node), so this
// returns 'Nugget'; nested PT Properties return 'ContentItem'.
const resolveContentParentType = (
    node: Partial<ContentItemViewNodesType>,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
): string | undefined => {
    const parentDtvId = node.dtvContentItemParentId || node.dtvParentId
    if (!parentDtvId) return undefined
    return contentItemViewNodes.find((n) => n.dtvItemId === parentDtvId)?.type
}

const createPropertyInput = (
    node: Partial<ContentItemViewNodesType>,
    parent: Partial<ContentItemViewNodesType>,
    text: string,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[] = []
): Partial<AddProjectContentItemInput> => {
    const uniqueKey = getUniqueKey(text)
    const parentKey = calculateParentKey(parent)
    const actualKeyWithParent = getActualKeyWithParent(
        uniqueKey || '',
        parentKey
    )

    const contentItemOwnerType = getContentOwnerType(node, parent)

    const isProductTypeProperty =
        node.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE ||
        parent.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE

    const contentItemParentType = isProductTypeProperty
        ? resolveContentParentType(node, contentItemViewNodes) ?? 'ContentItem'
        : parent.type ?? 'None'

    return {
        contentItemOwnerType,
        contentItemParentId: node.dtvContentItemParentId || node.dtvParentId,
        contentItemParentType,
        partType: node.partType,
        textType: 'Paragraph',
        productTypePropertyType: getProductTypePropertyType(node),
        index: node.order ?? node.paragraphOrder,
        propertyKey: actualKeyWithParent || undefined,
    }
}

/**
 * Custom hook for managing content item elements in Best Practices context.
 * Adapted from DraggableItem.tsx for use in the new workspace (Workspace2Store).
 *
 * Uses treeData for hierarchical operations since contentItemViewNodes is a flat array.
 */
export const useContentItemElements = () => {
    const treeData = useWorkspace2Store((state) => state.treeData)
    const contentItemViewNodes = useWorkspace2Store(
        (state) => state.contentItemViewNodes
    )

    /**
     * Builds a hierarchy map from treeData for quick parent/child lookups.
     * Similar to buildHierarchyMap in DraggableItem.tsx
     */
    const buildHierarchyMap = useCallback((nodes: DataNode[]): Map<
        string,
        HierarchyMapEntry
    > => {
        const hierarchyMap = new Map<string, HierarchyMapEntry>()

        const processNode = (currentNode: DataNode, parentNode?: DataNode) => {
            // Map by content item ID if available, otherwise by node key
            const contentItemId = currentNode.contentItemView?.id
            const nodeKey = contentItemId || currentNode.key

            hierarchyMap.set(nodeKey, {
                node: currentNode,
                parent: parentNode,
                contentItemView: currentNode.contentItemView,
            })

            // Process children recursively
            if (currentNode.children && currentNode.children.length > 0) {
                currentNode.children.forEach((childNode) => {
                    processNode(childNode, currentNode)
                })
            }
        }

        nodes.forEach((node) => processNode(node))
        return hierarchyMap
    }, [])
    /**
     * Creates property configuration for Best Practices for user-added content.
     * Adapted from getPropertyForSettingBestPracticesConfig in DraggableItem.tsx
     *
     * This function generates the metadata needed to recreate user-added content
     * when a Best Practice is applied to a different location.
     */
    const getPropertyForSettingBestPracticesConfig = useCallback(
        (
            node: Partial<ContentItemViewNodesType>,
            parent: Partial<ContentItemViewNodesType> | undefined,
            text: string
        ): Partial<AddProjectContentItemInput> => {
            const hasParent = !!parent

            // For Article (no parent)
            if (!hasParent) {
                return createArticleInput(node, text)
            }

            return createPropertyInput(
                node,
                parent!,
                text,
                contentItemViewNodes
            )
        },
        [contentItemViewNodes]
    )
    /**
     * Extracts elements from a content item node.
     * Adapted from getContentItemElements in DraggableItem.tsx
     */
    const getContentItemElements = useCallback(
        (
            contentItemView: Partial<ContentItemViewNodesType>,
            hierarchyMap: Map<string, HierarchyMapEntry>
        ): ElementData[] => {
            if (!contentItemView?.elements) {
                return []
            }

            const elements = buildContentElementsForSyncQuery(
                contentItemView.elements
            ) as ElementData[]

            // Handle user-added content for Smart Project Copy
            if (
                elements &&
                contentItemView.isUserAdded &&
                process.env.REACT_APP_EnableSmartProjectCopy === '1'
            ) {
                const nodeId = contentItemView.id
                const hierarchyEntry = nodeId
                    ? hierarchyMap.get(nodeId)
                    : undefined
                const parentContentItemView =
                    hierarchyEntry?.parent?.contentItemView

                // Get text from first element for property key derivation
                const text = (elements[0]?.text as string) || ''

                // Use getPropertyForSettingBestPracticesConfig to build the ref
                const ref = getPropertyForSettingBestPracticesConfig(
                    contentItemView,
                    parentContentItemView,
                    text
                )

                if (elements[0]) {
                    elements[0].userAddedContentBP = ref
                }
            }

            return elements
        },
        [getPropertyForSettingBestPracticesConfig]
    )

    /**
     * Recursively gets content item elements including children.
     * Adapted from getRecursiveContentItemElements in DraggableItem.tsx
     */
    const getRecursiveContentItemElements = useCallback(
        (
            node: DataNode,
            hierarchyMap: Map<string, HierarchyMapEntry>
        ): HierarchicalContentElement[] => {
            // Get elements from the current node's contentItemView
            const contentItemView = node.contentItemView
            if (!contentItemView) {
                return []
            }

            const currentElements = getContentItemElements(
                contentItemView,
                hierarchyMap
            )

            // Process each element to prepare for potential children
            // Convert ElementData to HierarchicalContentElement format
            const convertToHierarchical = (
                el: ElementData
            ): HierarchicalContentElement => {
                return {
                    id: el.id,
                    authoredUserId: el.authoredUserId,
                    imperialUnits: el.imperialUnits,
                    metricUnits: el.metricUnits,
                    optionItemState: el.optionItemState,
                    text: el.text,
                    type: el.type,
                    placeholderText: el.placeholderText,
                    userAddedContentBP: el.userAddedContentBP,
                    changeType: el.changeType,
                    children: el.elements?.map(convertToHierarchical),
                }
            }

            const hierarchicalElements: HierarchicalContentElement[] = currentElements.map(
                convertToHierarchical
            )

            // If node has children, process each child
            if (node.children && node.children.length > 0) {
                node.children.forEach((childNode) => {
                    const childHierarchicalElements = getRecursiveContentItemElements(
                        childNode,
                        hierarchyMap
                    )

                    // For each parent element, attach child elements based on match
                    hierarchicalElements.forEach((parentElement) => {
                        const matched = contentItemView.elements?.some(
                            (el) => el.id === parentElement.id
                        )

                        if (matched) {
                            if (!parentElement.children) {
                                parentElement.children = []
                            }

                            childHierarchicalElements.forEach((child) => {
                                parentElement.children!.push({
                                    ...child,
                                })
                            })
                        }
                    })
                })
            }

            return hierarchicalElements
        },
        [getContentItemElements]
    )

    /**
     * Gets recursive elements for a given content item ID.
     * Builds the hierarchy map and extracts elements with children.
     */
    const getRecursiveElementsForNode = useCallback(
        (contentItemId: string): HierarchicalContentElement[] => {
            // Build hierarchy map from treeData
            const hierarchyMap = buildHierarchyMap(treeData)

            // Find the node entry
            const nodeEntry = hierarchyMap.get(contentItemId)
            if (!nodeEntry) {
                return []
            }

            return getRecursiveContentItemElements(nodeEntry.node, hierarchyMap)
        },
        [treeData, buildHierarchyMap, getRecursiveContentItemElements]
    )

    /**
     * Gets elements for a given node (non-recursive, single node only).
     */
    const getElementsForNode = useCallback(
        (contentItemId: string): ElementData[] => {
            const hierarchyMap = buildHierarchyMap(treeData)
            const nodeEntry = hierarchyMap.get(contentItemId)

            if (!nodeEntry?.contentItemView) {
                return []
            }

            return getContentItemElements(
                nodeEntry.contentItemView,
                hierarchyMap
            )
        },
        [treeData, buildHierarchyMap, getContentItemElements]
    )

    return {
        buildHierarchyMap,
        getContentItemElements,
        getRecursiveContentItemElements,
        getRecursiveElementsForNode,
        getElementsForNode,
        getPropertyForSettingBestPracticesConfig,
    }
}
